import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {LabelPickerBaseProps, LabelPickerLabel} from '@github-ui/item-picker-label'
import {
  BulkIssueLabelPicker as BulkIssueLabelPickerTsq,
  type BulkIssueLabelPickerProps as BulkIssueLabelPickerTsqProps,
} from '@github-ui/item-picker-label-tsq/BulkIssueLabelPicker'
import {useCallback, useMemo} from 'react'
import {graphql, useClientQuery, useRelayEnvironment} from 'react-relay'

import type {BulkIssuesItemPickerLabelQuery} from './__generated__/BulkIssuesItemPickerLabelQuery.graphql'
import {addLabelsToStore, updateIssuesById} from './relay-label-interop'

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems'> & {
  labelNames?: string[]
}

type BulkItemPickerLabelProps = Omit<LabelPickerProps, 'onSelectionChange' | 'filterItems' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

/**
 * Dedicated GraphQL query for TSQ Label Picker preloaded data.
 *
 * This query is specifically designed for the TSQ (TanStack Query) Label Picker
 * to fetch label data that will be converted to preloaded format. Unlike the shared
 * LabelPickerClientQuery, this query uses inline fields instead of fragments,
 * allowing us to avoid fragment dependencies when passing data to the TSQ component.
 *
 * The TSQ Label Picker expects preloaded data to eliminate initial network requests
 * and provide immediate rendering of available labels, improving performance.
 *
 * This data is loaded into the Relay store in RelayRoute.tsx, where it reads data from
 * the `preloaded_records` key of the embedded data in the app.
 */
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PreloadedLabelsQuery = graphql`
  query BulkIssuesItemPickerLabelQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      id
      labels {
        # eslint-disable-next-line relay/unused-fields
        totalCount
        nodes {
          id
          # eslint-disable-next-line relay/unused-fields
          color
          # eslint-disable-next-line relay/unused-fields
          name
          # eslint-disable-next-line relay/unused-fields
          nameHTML
          # eslint-disable-next-line relay/unused-fields
          description
          # eslint-disable-next-line relay/unused-fields
          url
        }
      }
    }
  }
`

function usePreloadedLabels(owner: string, repo: string) {
  const preloadedData = useClientQuery<BulkIssuesItemPickerLabelQuery>(PreloadedLabelsQuery, {
    owner,
    repo,
  })

  // Transform Relay GraphQL response into TSQ-compatible preloaded data format
  return useMemo(() => {
    if (!preloadedData?.repository?.labels?.nodes?.length || !preloadedData?.repository?.id) {
      return undefined
    }

    const labels = (preloadedData.repository.labels.nodes || []).flatMap(node => {
      if (!node) return []
      return [node]
    })

    return {
      repositoryId: preloadedData.repository.id,
      labels,
      totalCount: preloadedData.repository.labels.totalCount || 0,
    }
  }, [preloadedData])
}

/**
 * Relay wrapper for bulk issue label picker with TSQ integration.
 *
 * This wrapper bridges Relay GraphQL data fetching and optimistic updates with the TSQ
 * (TanStack Query) bulk label picker. It provides:
 *
 * 1. Relay store optimistic updates via environment.commitUpdate
 * 2. Preloaded label data from Relay to TSQ component
 * 3. Integration with existing Relay mutation patterns
 *
 * The wrapper transforms label mutations into Relay store updates and ensures
 * the UI reflects changes immediately before server confirmation.
 */
export function BulkIssuesItemPickerLabelTsqWithRelayWrapper({
  connectionIds,
  existingIssueLabels,
  issuesToActOn,
  ...props
}: Omit<BulkItemPickerLabelProps, 'onSelectionChanged'>) {
  const environment = useRelayEnvironment()
  const onMutate: BulkIssueLabelPickerTsqProps['onMutate'] = useCallback(
    input => {
      environment.commitUpdate(store => {
        // Add labels to store if they're being added and labels data is provided
        if (input.addedLabelIds.length > 0 && input.labels) {
          // Filter labels to only those being added
          const labelsToAdd = input.labels.filter(label => input.addedLabelIds.includes(label.id))
          addLabelsToStore(store, labelsToAdd)
        }

        const inputArgs = {
          applyLabelIds: input.addedLabelIds,
          removeLabelIds: input.removedLabelIds,
        }
        updateIssuesById(store, issuesToActOn, existingIssueLabels, connectionIds, inputArgs)
      })
    },
    [connectionIds, environment, existingIssueLabels, issuesToActOn],
  )

  const preloadedLabels = usePreloadedLabels(props.owner, props.repo)

  return (
    <BulkIssueLabelPickerTsq
      {...props}
      issuesToActOn={issuesToActOn}
      onMutate={onMutate}
      preloadedLabels={preloadedLabels}
    />
  )
}

// Re-export with a shorter name for backward compatibility
export {BulkIssuesItemPickerLabelTsqWithRelayWrapper as BulkIssuesItemPickerLabel}

try{ BulkIssuesItemPickerLabelTsqWithRelayWrapper.displayName ||= 'BulkIssuesItemPickerLabelTsqWithRelayWrapper' } catch {}